/**
 * Comprehensive Terms & Conditions Template Generator
 * 
 * Generates contest terms based on structured field data.
 * Gracefully omits sections when fields are blank.
 */

export interface TermsTemplateData {
    // Sponsor Information
    sponsorName: string;
    sponsorAddress: string;
    supportEmail: string;

    // Eligibility
    eligibleRegions: string;
    minimumAge: string;
    excludedStates?: string; // e.g., "Florida, New York"

    // Timing
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;

    // Entry Methods
    entryMethods: string; // Description of online entry methods
    mailInAddress?: string; // If provided, shows mail-in option
    entryLimit?: string; // e.g., "one entry per person per day"

    // Prizes
    prizeDescription: string;
    prizeArv: string;
    prizeQuantity?: string; // e.g., "One (1)" or "Three (3)"
    totalPrizeValue?: string; // If multiple prizes

    // Winner Selection
    winnerNotificationMethod: string;
    winnerNotificationTimeframe: string;
    responseWindow: string;
    winnerSelectionDate?: string;

    // Referral Program
    referralBonusEntries: number;

    // Legal
    governingState: string;

    // Optional Marketing
    includeMarketingOptIn?: boolean;
}

export function generateTermsTemplate(data: TermsTemplateData): string {
    const sections: string[] = [];

    // Header
    sections.push(`NO PURCHASE NECESSARY TO ENTER OR WIN. A PURCHASE WILL NOT INCREASE YOUR CHANCES OF WINNING.`);
    sections.push('');

    // 1. Sponsor
    sections.push(`**1. SPONSOR**`);
    sections.push('');
    sections.push(`This promotion (the "Promotion" or "Contest") is sponsored by ${data.sponsorName}, located at ${data.sponsorAddress} ("Sponsor").`);
    sections.push('');

    // 2. Eligibility
    sections.push(`**2. ELIGIBILITY**`);
    sections.push('');
    sections.push(`The Promotion is open only to legal residents of ${data.eligibleRegions} who are at least ${data.minimumAge} years of age or older at the time of entry.`);
    sections.push('');

    if (data.excludedStates) {
        sections.push(`This Promotion is void in ${data.excludedStates} and where prohibited or restricted by law.`);
        sections.push('');
    }

    sections.push(`Employees, officers, directors, and agents of Sponsor, and members of their immediate families (spouse, parent, child, sibling, and their respective spouses, regardless of where they reside) and persons living in the same household, whether or not related, are not eligible to participate or win.`);
    sections.push('');

    // 3. Agreement to Official Rules
    sections.push(`**3. AGREEMENT TO OFFICIAL RULES**`);
    sections.push('');
    sections.push(`By participating in this Promotion, you agree to be bound by these Official Rules and the decisions of Sponsor, which are final and binding in all respects. Winning a prize is contingent upon fulfilling all requirements set forth herein.`);
    sections.push('');

    // 4. Promotion Period
    sections.push(`**4. PROMOTION PERIOD**`);
    sections.push('');
    sections.push(`The Promotion begins on ${data.startDate} at ${data.startTime} and ends on ${data.endDate} at ${data.endTime} (the "Promotion Period"). All entries must be received during the Promotion Period. Sponsor's computer is the official time-keeping device for this Promotion.`);
    sections.push('');

    // 5. How to Enter
    sections.push(`**5. HOW TO ENTER**`);
    sections.push('');
    sections.push(`**Online Entry:** ${data.entryMethods} Each completed action will award the number of entries specified on the Promotion page.`);
    sections.push('');

    if (data.mailInAddress) {
        sections.push(`**Mail-In Entry:** To enter without completing online actions, hand-print your full name, complete mailing address, telephone number, date of birth, and email address on a 3" x 5" card and mail it in a stamped envelope to: ${data.mailInAddress}. Mail-in entries must be postmarked during the Promotion Period and received within five (5) business days after the end of the Promotion Period. Each mail-in entry will be awarded one (1) entry. Limit one mail-in entry per outer envelope.`);
        sections.push('');
    }

    if (data.entryLimit) {
        sections.push(`**Entry Limit:** ${data.entryLimit}.`);
        sections.push('');
    }

    sections.push(`All entries become the property of Sponsor and will not be acknowledged or returned. Use of automated entry devices or programs is prohibited and will result in disqualification.`);
    sections.push('');

    // 6. Referral Program
    if (data.referralBonusEntries > 0) {
        sections.push(`**6. REFERRAL PROGRAM**`);
        sections.push('');
        sections.push(`Participants may earn additional entries by sharing their unique referral link with others. For each new, eligible participant who enters the Promotion using your referral link, you will receive ${data.referralBonusEntries} bonus ${data.referralBonusEntries === 1 ? 'entry' : 'entries'}. Referrals must be genuine; self-referrals, fake accounts, or fraudulent referrals will result in disqualification of both the referrer and referee.`);
        sections.push('');
    }

    // 7. Prizes
    const sectionNum = data.referralBonusEntries > 0 ? 7 : 6;
    sections.push(`**${sectionNum}. PRIZE(S)**`);
    sections.push('');

    const prizeQty = data.prizeQuantity || "One (1)";
    sections.push(`**Prize:** ${prizeQty} ${data.prizeDescription}. Approximate Retail Value ("ARV"): ${data.prizeArv}.`);
    sections.push('');

    if (data.totalPrizeValue) {
        sections.push(`**Total ARV of all prizes:** ${data.totalPrizeValue}.`);
        sections.push('');
    }

    sections.push(`Prizes are awarded "as is" with no warranty or guarantee, either express or implied. Winners are responsible for all applicable federal, state, and local taxes, if any, as well as any other costs and expenses associated with prize acceptance and use not specified herein as being provided. No substitution, assignment, or transfer of prize permitted, except by Sponsor, who reserves the right to substitute a prize of equal or greater value in the event the advertised prize is unavailable.`);
    sections.push('');

    // 8. Odds of Winning
    const nextNum = sectionNum + 1;
    sections.push(`**${nextNum}. ODDS OF WINNING**`);
    sections.push('');
    sections.push(`Odds of winning depend on the total number of eligible entries received during the Promotion Period.`);
    sections.push('');

    // 9. Winner Selection and Notification
    const winnerNum = nextNum + 1;
    sections.push(`**${winnerNum}. WINNER SELECTION AND NOTIFICATION**`);
    sections.push('');

    const selectionDate = data.winnerSelectionDate || `within ${data.winnerNotificationTimeframe} after the end of the Promotion Period`;
    sections.push(`The potential winner(s) will be selected in a random drawing from among all eligible entries received during the Promotion Period. The drawing will be conducted ${selectionDate} by Sponsor or its designated representative, whose decisions are final and binding.`);
    sections.push('');
    sections.push(`The potential winner will be notified via ${data.winnerNotificationMethod} within ${data.winnerNotificationTimeframe} after the drawing. If a potential winner does not respond within ${data.responseWindow} of notification, or if the notification is returned as undeliverable, or if the potential winner is found to be ineligible or not in compliance with these Official Rules, the prize will be forfeited and an alternate winner may be selected.`);
    sections.push('');
    sections.push(`The potential winner may be required to complete, sign, and return an Affidavit of Eligibility, Liability Release, and (where legal) Publicity Release within the time period specified in the notification. Failure to comply may result in disqualification and selection of an alternate winner.`);
    sections.push('');

    // 10. Rights Granted by Entrants
    const rightsNum = winnerNum + 1;
    sections.push(`**${rightsNum}. RIGHTS GRANTED BY ENTRANTS**`);
    sections.push('');
    sections.push(`By entering the Promotion, you grant Sponsor and its designees a perpetual, worldwide, royalty-free license to use, reproduce, modify, publish, and display your entry and any materials submitted in connection with the Promotion for promotional, marketing, and other business purposes. Except where prohibited by law, acceptance of a prize constitutes permission for Sponsor to use winner's name, likeness, voice, and biographical information for promotional purposes without further compensation or notice.`);
    sections.push('');

    // 11. General Conditions / Sponsor Rights
    const conditionsNum = rightsNum + 1;
    sections.push(`**${conditionsNum}. GENERAL CONDITIONS AND SPONSOR RIGHTS**`);
    sections.push('');
    sections.push(`Sponsor reserves the right, in its sole discretion, to cancel, terminate, modify, or suspend the Promotion (or any portion thereof) if fraud, technical failures, human error, or any other factor beyond Sponsor's reasonable control impairs the integrity or proper functioning of the Promotion, as determined by Sponsor in its sole discretion.`);
    sections.push('');
    sections.push(`Sponsor reserves the right to disqualify any individual found to be tampering with the entry process, the operation of the Promotion, or violating these Official Rules. Any attempt to deliberately damage any website or undermine the legitimate operation of the Promotion is a violation of criminal and civil laws, and should such an attempt be made, Sponsor reserves the right to seek damages and other remedies to the fullest extent permitted by law.`);
    sections.push('');
    sections.push(`Entries generated by script, macro, bot, or other automated means, or entries by any means which subvert the entry process, are void. In the event of a dispute regarding entries received from multiple users having the same email account, the authorized subscriber of the email account at the time of entry will be deemed to be the entrant and must comply with these Official Rules.`);
    sections.push('');

    // 12. Marketing Opt-In (Optional)
    if (data.includeMarketingOptIn) {
        const marketingNum = conditionsNum + 1;
        sections.push(`**${marketingNum}. MARKETING COMMUNICATIONS**`);
        sections.push('');
        sections.push(`By entering this Promotion, you may opt in to receive promotional emails and communications from Sponsor. You may opt out at any time by following the unsubscribe instructions in any email or by contacting ${data.supportEmail}. Opting out of marketing communications will not affect your eligibility to participate or win.`);
        sections.push('');
    }

    // 13. Limitation of Liability
    const liabilityNum = data.includeMarketingOptIn ? conditionsNum + 2 : conditionsNum + 1;
    sections.push(`**${liabilityNum}. LIMITATION OF LIABILITY**`);
    sections.push('');
    sections.push(`By participating, you agree to release, discharge, and hold harmless Sponsor, its parent companies, subsidiaries, affiliates, and their respective officers, directors, employees, and agents (collectively, the "Released Parties") from any and all liability, loss, or damage arising from or in connection with: (a) participation in the Promotion; (b) the acceptance, possession, use, or misuse of any prize; and (c) any claims based on publicity rights, defamation, invasion of privacy, copyright infringement, trademark infringement, or any other intellectual property-related cause of action.`);
    sections.push('');
    sections.push(`The Released Parties are not responsible for: (i) lost, late, incomplete, stolen, misdirected, illegible, or postage-due entries; (ii) errors or difficulties of any kind, whether human, mechanical, electronic, network, printing, typographical, or otherwise, relating to or in connection with the Promotion; (iii) failed, incomplete, garbled, or delayed computer transmissions; (iv) technical malfunctions of telephone networks, computer systems, computer online systems, servers, providers, hardware, or software; or (v) any injury or damage to your or any other person's computer related to or resulting from participating in the Promotion.`);
    sections.push('');
    sections.push(`IN NO EVENT WILL THE RELEASED PARTIES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES ARISING OUT OF OR RELATED TO YOUR PARTICIPATION IN THE PROMOTION, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.`);
    sections.push('');

    // 14. Disputes and Governing Law
    const disputesNum = liabilityNum + 1;
    sections.push(`**${disputesNum}. DISPUTES AND GOVERNING LAW**`);
    sections.push('');
    sections.push(`Except where prohibited, by entering the Promotion, you agree that: (a) any and all disputes, claims, and causes of action arising out of or connected with the Promotion or any prize awarded shall be resolved individually, without resort to any form of class action; (b) any and all claims, judgments, and awards shall be limited to actual out-of-pocket costs incurred, but in no event attorneys' fees; and (c) under no circumstances will you be permitted to obtain awards for, and you hereby waive all rights to claim, punitive, incidental, and consequential damages and any other damages, other than for actual out-of-pocket expenses, and any and all rights to have damages multiplied or otherwise increased.`);
    sections.push('');
    sections.push(`This Promotion and these Official Rules are governed by the laws of the State of ${data.governingState}, without regard to its conflict of laws principles. By entering, you consent to the jurisdiction and venue of the state and federal courts located in ${data.governingState} for the resolution of any disputes.`);
    sections.push('');

    // 15. Privacy Policy
    const privacyNum = disputesNum + 1;
    sections.push(`**${privacyNum}. PRIVACY POLICY**`);
    sections.push('');
    sections.push(`Information collected from entrants is subject to Sponsor's Privacy Policy and the RaffleKit Privacy Policy. By entering, you acknowledge that you have read and agree to these privacy policies.`);
    sections.push('');

    // 16. Winners List
    const winnersNum = privacyNum + 1;
    sections.push(`**${winnersNum}. WINNERS LIST**`);
    sections.push('');
    sections.push(`For the name of the winner(s), send a self-addressed stamped envelope to ${data.sponsorAddress}, Attn: "${data.sponsorName} Promotion Winner," within sixty (60) days after the end of the Promotion Period. Requests received after this time period will not be honored.`);
    sections.push('');

    // 17. Platform Disclaimer
    const platformNum = winnersNum + 1;
    sections.push(`**${platformNum}. PLATFORM DISCLAIMER**`);
    sections.push('');
    sections.push(`This Promotion is in no way sponsored, endorsed, administered by, or associated with any social media platform or third-party service used to promote or facilitate the Promotion. You understand that you are providing your information to Sponsor and not to any third-party platform. Any questions, comments, or complaints regarding the Promotion should be directed to Sponsor at ${data.supportEmail}, not to any third-party platform.`);
    sections.push('');

    // 18. Contact Information
    const contactNum = platformNum + 1;
    sections.push(`**${contactNum}. SPONSOR CONTACT INFORMATION**`);
    sections.push('');
    sections.push(`For questions about this Promotion or these Official Rules, please contact:`);
    sections.push(`${data.sponsorName}`);
    sections.push(`${data.sponsorAddress}`);
    sections.push(`Email: ${data.supportEmail}`);

    return sections.join('\n');
}
